/**
 * Terms of Service Page
 * 
 * Displays the terms of service for using Kirjuri application.
 */

import { useState, useEffect } from 'react';
import { Card, PageHeader, PageLoading } from '../components';

interface TermsSection {
  title: string;
  content: string[];
}

const TERMS_CONTENT: TermsSection[] = [
  {
    title: '1. Yleistä',
    content: [
      'Nämä käyttöehdot ("ehdot") koskevat Kirjuri-palvelun ("palvelu") käyttöä.',
      'Palvelua ylläpitää Kirstunvartija. Käyttämällä palvelua hyväksyt nämä ehdot.',
      'Jos et hyväksy näitä ehtoja, älä käytä palvelua.',
    ],
  },
  {
    title: '2. Palvelun kuvaus',
    content: [
      'Kirjuri on kirjanpito- ja taloushallintopalvelu yhdistyksille ja pienille organisaatioille.',
      'Palvelu mahdollistaa tapahtumien seurannan, tositteiden hallinnan, laskutuksen ja raportoinnin.',
      'Palvelu on pilvipalvelu, joka toimii verkkoselaimella.',
    ],
  },
  {
    title: '3. Käyttäjätili',
    content: [
      'Palvelun käyttäminen edellyttää käyttäjätilin luomista.',
      'Olet vastuussa tilitietojesi salassapidosta ja kaikista tililläsi tapahtuvista toiminnoista.',
      'Ilmoitamme välittömästi epäilyttävästä toiminnasta tililläsi.',
      'Voit poistaa tilisi milloin tahansa GDPR-asetusten kautta.',
    ],
  },
  {
    title: '4. Tietoturva',
    content: [
      'Käytämme alan standardien mukaista tietoturvaa suojellaksemme tietojasi.',
      'Tiedot salataan sekä siirrossa että levossa.',
      'Käytämme kahden tekijän tunnistautumista (2FA) kriittisiin toimintoihin.',
      'Sinun tulee käyttää vahvoja salasanoja ja pitää ne turvassa.',
    ],
  },
  {
    title: '5. Datan omistus ja säilytys',
    content: [
      'Kaikki syöttämäsi data kuuluu sinulle tai organisaatiollesi.',
      'Voit viedä datasi milloin tahansa GDPR-työkalujen avulla.',
      'Kirjanpitotiedot säilytetään lakisääteisen määräajan (6 vuotta) vaikka poistaisit tilisi.',
      'Henkilötiedot poistetaan tilin poiston yhteydessä tai aiemmin pyynnöstä.',
    ],
  },
  {
    title: '6. Käyttörajoitukset',
    content: [
      'Et saa käyttää palvelua laittomiin tarkoituksiin.',
      'Et saa yrittää murtautua palvelun tietoturvaan.',
      'Et saa käyttää palvelua muiden käyttäjien häiritsemiseen.',
      'Et saa lähettää haittakoodia tai viruksia palvelun kautta.',
    ],
  },
  {
    title: '7. Palvelun saatavuus',
    content: [
      'Pyrimme 99.9%:n saatavuuteen, mutta emme takaa keskeytyksetöntä toimintaa.',
      'Huoltokatkokset ilmoitetaan etukäteen mahdollisuuksien mukaan.',
      'Emme ole vastuussa välillisistä vahingoista palvelun käytöstä tai käyttökatkoksista.',
    ],
  },
  {
    title: '8. Maksuehdot',
    content: [
      'Palvelu voi sisältää maksullisia ominaisuuksia.',
      'Hinnat ilmoitetaan selkeästi ennen ostopäätöstä.',
      'Maksut laskutetaan valitun jakson mukaisesti.',
      'Voit peruuttaa tilauksen milloin tahansa.',
    ],
  },
  {
    title: '9. Ehtojen muutokset',
    content: [
      'Voimme päivittää näitä ehtoja ajoittain.',
      'Merkittävistä muutoksista ilmoitetaan sähköpostitse tai palvelun kautta.',
      'Jatkamalla palvelun käyttöä muutosten jälkeen hyväksyt uudet ehdot.',
    ],
  },
  {
    title: '10. Yhteystiedot',
    content: [
      'Kysymykset käyttöehdoista: support@kirjuri.app',
      'Tietosuojaan liittyvät kysymykset: privacy@kirjuri.app',
      'Postiosoite: [Yrityksen osoite]',
    ],
  },
];

export default function TermsOfServicePage() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading for consistent UX
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <PageLoading message="Ladataan käyttöehtoja..." />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageHeader
        title="📄 Käyttöehdot"
        subtitle="Viimeksi päivitetty: 27. helmikuuta 2026"
      />

      <div className="space-y-6">
        {TERMS_CONTENT.map((section, index) => (
          <Card key={index}>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {section.title}
            </h2>
            <div className="space-y-3 text-gray-700">
              {section.content.map((paragraph, pIndex) => (
                <p key={pIndex} className="leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </Card>
        ))}

        {/* Acceptance notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Huom:</strong> Käyttämällä Kirjuri-palvelua hyväksyt nämä käyttöehdot 
            ja sitoudut noudattamaan niitä. Jos sinulla on kysyttävää ehdoista, 
            ota yhteyttä asiakastukeen ennen palvelun käyttöä.
          </p>
        </div>
      </div>
    </div>
  );
}
